
import {
  CargoV2SearchFlight,
  CargoV2SearchFlightItinerary,
} from "@/domain/entities/CargoV2";
import moment from "moment";
import { Vue, Options, prop } from "vue-class-component";
import { getAirportIcon } from "../../modules/airport-icons-circle";
import { CargoV2Controller } from '@/app/ui/controllers/CargoV2Controller';
import { FlagsPermissionCargoV2 } from '@/feature-flags/flags-permission-cargo-v2';
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  flight = prop<CargoV2SearchFlight>({
    default: new CargoV2SearchFlight(),
    type: CargoV2SearchFlight,
  });
  isSelected = prop<boolean>({
    default: false,
    type: Boolean,
  });
}

@Options({})
class CardFlight extends Vue.with(Props) {
  isTransitOpen = false;
  isSelected = false;

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  formatCapacity(value: number) {
    return formatByCountry(value ?? 0, { type: "weight", country: this.countryCode });
  }

  getFlightIcon(flightNo: string) {
    return getAirportIcon(flightNo);
  }
  get lowestTransitCapacity() {
    const itineraries = JSON.parse(JSON.stringify(this.flight.itineraries)) as CargoV2SearchFlightItinerary[];
    return (
      itineraries.sort(
        (a, b) => a.remainingCapacity - b.remainingCapacity,
      )[0] ?? null
    );
  }
  toggleTransit(event: Event) {
    this.isTransitOpen = !this.isTransitOpen;
    event.stopPropagation();
    event.preventDefault();
  }
  get isLoadingSubmit() {
    return CargoV2Controller.isLoadingSubmitSearchFlight;
  }
  get firstFlightNo() {
    return this.flight.itineraries[0]?.flightNo ?? "";
  }
  get isGridLayout() {
    return this.flight.itineraries.length > 4;
  }
  get transitGridRows() {
    const items = [
      ...this.flight.itineraries.map((transit, index) => ({
        isDestination: false,
        isEmpty: false,
        transit,
        index,
      })),
      {
        isDestination: true,
        isEmpty: false,
        transit: this.flight.itineraries[this.flight.itineraries.length - 1],
        index: -1,
      },
    ];
    const rows = [];
    const firstRow = items.slice(0, 4) as typeof items;
    while (firstRow.length < 4) {
      firstRow.push({ isEmpty: true, isDestination: false, transit: null as any, index: -1 });
    }
    rows.push(firstRow);
    for (let i = 4; i < items.length; i += 3) {
      const row = items.slice(i, i + 3) as typeof items;
      while (row.length < 3) {
        row.push({ isEmpty: true, isDestination: false, transit: null as any, index: -1 });
      }
      rows.push(row);
    }
    return rows;
  }
  get transitLabel() {
    const via =
      this.flight.isFlightVia ? " (via)" : "";

    if (!this.flight.isTransit) {
      return `${this.$t("cargo.v2.flight.search.card.direct")} ${via}`;
    }

    return `${this.flight.transitNumber} transit${via}`;
  }
  get flightDisabled() {
    return (
      !this.flight.isVolumeAvailability ||
      !this.flight.capacityAvailable ||
      this.flight.embargo ||
      !this.flight.isDgAllowed ||
      !this.flight.dimensionAvailable
    );
  }
  get departureHours() {
    const totalMinutes = moment(this.flight.departureDate).diff(
      moment(),
      "minutes",
    );
    return Math.max(0, Math.floor(totalMinutes / 60));
  }
  get departureMinutes() {
    const totalMinutes = moment(this.flight.departureDate).diff(
      moment(),
      "minutes",
    );
    return Math.max(0, totalMinutes % 60);
  }
  get useFcsHistoryDepartureBadge() {
    return FlagsPermissionCargoV2.permission_history_departure_status.isEnabled();
  }
  formatDateShort(date: string) {
    const currentLocale = this.$i18n.locale === "my" ? "en" : this.$i18n.locale;
    return date
      ? moment(date)
          .locale(currentLocale)
          .format("ddd, DD MMM")
      : "";
  }
  formatDateTransit(date: string) {
    const currentLocale = this.$i18n.locale === "my" ? "en" : this.$i18n.locale;
    return date
      ? moment(date)
          .locale(currentLocale)
          .format("DD MMM")
      : "";
  }
  formatDuration(totalMinutes: number) {
    if (totalMinutes <= 0)
      return this.$t("cargo.v2.flight.search.card.durationZero");
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours === 0)
      return this.$t("cargo.v2.flight.search.card.durationMinutes", { mins });
    if (mins === 0)
      return this.$t("cargo.v2.flight.search.card.durationHours", { hours });
    return this.$t("cargo.v2.flight.search.card.durationHoursMinutes", {
      hours,
      mins
    });
  }
  formatTime(time: string) {
    return moment(time).format("HH:mm");
  }
  get arrivalDayDiff(): number {
    if (!this.flight.departureDate || !this.flight.arrivalDate) return 0;
    return moment(this.flight.arrivalDate)
      .startOf("day")
      .diff(moment(this.flight.departureDate).startOf("day"), "days");
  }

  handleClickCard() {
    this.$emit("select", this.flight);
  }
}
export default CardFlight;
